<template>
  <div v-tooltip="disableWith">
    <component :is="buttonComponent" :disabled="disabled" @click="clicked">
      <div
        v-if="waiting"
        class="sf-spinner sf-spinner-primary"
        data-testid="mark-as-finalized-spinner"
      />
      {{ text }}
    </component>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watchEffect, onBeforeUnmount } from 'vue'
import { storeToRefs } from 'pinia'
import SfButton from '@app/shared/vue/components/sf-button/sf-button.vue'
import SfButtonInverted from '@app/shared/vue/components/sf-button/sf-button-inverted.vue'
import { usePulseWorkflowProgress } from '@app/application/workflow-overview-navigation/store/pulse-workflow-progress'

import { WORKFLOW_PROGRESS_TIMEOUT } from '../constants'
import {
  WorkflowProgressStatus,
  WorkflowProgressStatuses,
  SaveChangesStatus,
  SaveChangesStatuses,
} from '../types'

interface Props {
  text: string
  withWorkflowProgressTracking?: boolean
  disableWith?: string | undefined
}

const props = withDefaults(defineProps<Props>(), {
  withWorkflowProgressTracking: false,
  disableWith: undefined,
})

onBeforeUnmount(() => {
  clearTimeout(waitingForProgressTimeoutId)
})

// eslint-disable-next-line no-undef
defineOptions({
  name: 'MarkAsFinalizedButton',
})

const disabled = computed(
  () => props.disableWith !== undefined || waiting.value
)

const workflowProgressStore = usePulseWorkflowProgress()

const { lastReceived: lastWorkflowProgressAt, workflowProgress } = storeToRefs(
  workflowProgressStore
)

const saveChangesStatus = ref<SaveChangesStatus>(SaveChangesStatuses.INITIAL)
const workflowProgressStatus = ref<WorkflowProgressStatus>(
  WorkflowProgressStatuses.INITIAL
)
// need to track if we received a new Workflow Progress payload _after_ we clicked on the button
const previousLastWorkflowProgressAt = ref<number>(0)
let waitingForProgressTimeoutId = -1

const buttonComponent = computed(() =>
  completed.value ? SfButton : SfButtonInverted
)

const completed = computed(
  () => workflowProgress.value === 100 || !props.withWorkflowProgressTracking
)
// show spinner and disable if:
// we're waiting for the workflow progress AND save changes didn't error out
// (i.e. if save changes threw an error, we hide the spinner without waiting for the WF progress)
const waiting = computed(
  () =>
    workflowProgressStatus.value === WorkflowProgressStatuses.WAITING &&
    saveChangesStatus.value !== SaveChangesStatuses.ERROR
)

const openModal = (): void => {
  window.modalCitizen.openModalPortal('mark-proposal-as-finalized-modal')
}

const clicked = (): void => {
  previousLastWorkflowProgressAt.value = lastWorkflowProgressAt.value
  saveChanges()

  props.withWorkflowProgressTracking
    ? triggerWaitingForProgress()
    : completeWaitingForProgress()
}

function saveChanges() {
  window.eventHub.$emit('saveChanges', {
    successCallback: () => {
      saveChangesStatus.value = SaveChangesStatuses.SUCCESS
    },
    errorCallback: () => {
      saveChangesStatus.value = SaveChangesStatuses.ERROR
    },
  })
}

function reset(): void {
  saveChangesStatus.value = SaveChangesStatuses.INITIAL
  workflowProgressStatus.value = WorkflowProgressStatuses.INITIAL
  workflowProgressStore.clearLastReceived()
}

function triggerWaitingForProgress(): void {
  workflowProgressStatus.value = WorkflowProgressStatuses.WAITING

  waitingForProgressTimeoutId = window.setTimeout(
    completeWaitingForProgress,
    WORKFLOW_PROGRESS_TIMEOUT
  )
}

function completeWaitingForProgress(): void {
  workflowProgressStatus.value = WorkflowProgressStatuses.COMPLETED
}

watchEffect(() => {
  // While we are waiting check if lastReceived is newer than the moment of the click,
  // and if so, complete the waiting
  if (
    workflowProgressStatus.value === WorkflowProgressStatuses.WAITING &&
    lastWorkflowProgressAt.value > previousLastWorkflowProgressAt.value
  ) {
    completeWaitingForProgress()
  }
})

watchEffect(() => {
  // Both the operations have completed, open the modal and reset the state
  if (
    saveChangesStatus.value === SaveChangesStatuses.SUCCESS &&
    workflowProgressStatus.value === WorkflowProgressStatuses.COMPLETED
  ) {
    openModal()
    reset()
  }
})
</script>
